import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, computed, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActionRole, ButtonType, Level } from '@newroom-connect/library/enums';
import { IFileUploadItem } from '@newroom-connect/library/interfaces';
import { FormControlPipe } from '@newroom-connect/library/pipes';

import { ButtonComponent } from '../../buttons/button/button.component';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { IconComponent } from '../../icon/icon.component';
import { InputCheckboxComponent } from '../../inputs/input-checkbox/input-checkbox.component';
import { MediaPreviewComponent } from '../../media-preview/media-preview.component';
import { InputLanguageSelectorComponent, InputLanguageSelectorComponentType } from '../../inputs/input-language-selector/input-language-selector.component';

/**
 * Interface for translation parameters emitted by the component.
 */
export interface FileTranslationParams {
  fileId: string;
  fileName: string;
  sourceLanguage: string;
  targetLanguage: string;
  exportAsPdf: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FileUploaderComponent,
    IconComponent,
    MediaPreviewComponent,
    InputCheckboxComponent,
    FormControlPipe,
    ButtonComponent,
    InputLanguageSelectorComponent
  ],
  selector: 'nrc-file-translation',
  templateUrl: './file-translation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileTranslationComponent implements OnInit {
  @Input() public projectId = '';
  @Input() public sourceLanguageCodes: string[] = [];
  @Input() public targetLanguageCodes: string[] = [];
  @Input() public translationModalTitle = 'Translate File';
  @Input() public translationModalDescription = 'Upload a file and select languages for translation.';

  // Allow parent component to set uploaded file ID
  @Input() public set uploadedFileId(fileId: string) {
    if (fileId) {
      this.fileId.set(fileId);
    }
  }

  @Output() public closeEvent = new EventEmitter<void>();
  @Output() public fileChangedEvent = new EventEmitter<IFileUploadItem>();
  @Output() public fileSizeLimitExceededEvent = new EventEmitter<File>();
  @Output() public translateEvent = new EventEmitter<FileTranslationParams>();

  public selectedFile = signal<File | null>(null);
  public fileId = signal<string>('');
  public filteredTargetLanguageCodes = signal<string[]>([]);

  /**
   * Computed signal that returns the MIME type based on the selected file's extension.
   */
  public fileMimetype = computed(() => {
    const file = this.selectedFile();

    if (!file) {
      return '';
    }

    // Extract extension from the file name
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    // Map extensions to MIME types
    switch (extension) {
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'txt':
        return 'text/plain';
      case 'html':
        return 'text/html';
      default:
        return 'application/octet-stream';
    }
  });

  public level = Level;
  public buttonType = ButtonType;
  public actionRole = ActionRole;
  public inputLanguageSelectorComponentType = InputLanguageSelectorComponentType;

  public fileTranslateFormGroup: FormGroup;

  constructor() {
    this.fileTranslateFormGroup = new FormGroup({
      from: new FormControl('', [Validators.required]),
      to: new FormControl('', [Validators.required]),
      exportToPdf: new FormControl(true)
    });
  }

  /**
   * Initialize component.
   */
  public ngOnInit(): void {
    // Set default source language as the first item in the source list if available
    if (this.sourceLanguageCodes.length > 0) {
      this.fileTranslateFormGroup.controls['from'].patchValue(this.sourceLanguageCodes[0]);
    }

    if (this.targetLanguageCodes.length > 0) {
      this.fileTranslateFormGroup.controls['to'].patchValue(this.targetLanguageCodes[0]);
    }

    this.updateFilteredTargetLanguages();
  }

  /**
   * Handle file change event from the file uploader.
   *
   * @param files The uploaded files.
   */
  public handleFileChange(files: IFileUploadItem[]): void {
    if (files.length > 0) {
      this.selectedFile.set(files[0].file);
      this.fileChangedEvent.emit(files[0]);
    }
  }

  /**
   * Set the file ID after it has been uploaded by the parent component.
   *
   * @param id
   */
  public setFileId(id: string): void {
    this.fileId.set(id);
  }

  /**
   * Updates the filtered target languages, excluding the source language.
   */
  public updateFilteredTargetLanguages(): void {
    const fromValue = this.fileTranslateFormGroup.controls['from'].value;

    // Filter out source language from target options
    const filteredLanguages = this.targetLanguageCodes.filter(
      lang => lang !== fromValue
    );

    this.filteredTargetLanguageCodes.set(filteredLanguages);

    // Clear if current target matches the source language
    const currentTarget = this.fileTranslateFormGroup.controls['to'].value as string;

    if (currentTarget && currentTarget === fromValue) {
      this.fileTranslateFormGroup.controls['to'].setValue('');
    }
  }

  /**
   * Check if translation can be enabled.
   * @returns Whether the translate button should be enabled.
   */
  public isTranslateEnabled(): boolean {
    const targetLanguage = this.fileTranslateFormGroup.controls['to'].value as string;

    return !!this.selectedFile() &&
        !!this.fileId() &&
        !!this.fileTranslateFormGroup.controls['from'].value &&
        !!targetLanguage;
  }

  /**
   * Handle file translation request by emitting parameters to parent.
   */
  public handleTranslateFile(): void {
    const selectedFile = this.selectedFile();
    const fileId = this.fileId();
    const isTranslateEnabled = this.isTranslateEnabled();

    if (!selectedFile || !fileId || !isTranslateEnabled) {
      return;
    }

    const sourceLanguage = this.fileTranslateFormGroup.controls['from'].value;
    const targetLanguage = this.fileTranslateFormGroup.controls['to'].value as string;
    const exportAsPdf = this.fileTranslateFormGroup.controls['exportToPdf'].value;

    // Emit translation parameters to parent component
    this.translateEvent.emit({
      fileId,
      fileName: selectedFile.name,
      sourceLanguage,
      targetLanguage,
      exportAsPdf
    });
  }
}
